// DTO → view model for the property rent ledger.
//
// Money stays a STRING. Never Number() a money field here — these figures sit
// beside wallet balances, and a float artefact on a screen about investors'
// money is a defect (ux-principles §9).

import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  PropertyBillDto,
  PropertyRentLedgerDetailDto,
  PropertyRentLedgerDto,
  RentLedgerListDto,
  ReserveMovementDto,
  ProvisionedByKindDto,
  BillPaymentPermissionDto,
} from './rentLedgerContracts'
import type {
  PropertyBill,
  PropertyRentLedgerDetail,
  PropertyRentLedgerRow,
  ProvisionedByKind,
  ReserveMovement,
  BillPaymentPermission,
} from '../types/rentLedgerTypes'

const ZERO = '0.00'
const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20

/** Money default — keeps every amount a string even when the wire omits it. */
function money(value: string | null | undefined): string {
  return value ?? ZERO
}

export function parsePropertyRentLedgerRow(dto: PropertyRentLedgerDto): PropertyRentLedgerRow {
  return {
    propertyId: dto.propertyId,
    propertyName: dto.propertyName,
    propertyRefCode: dto.propertyRefCode ?? null,
    currencyCode: dto.currencyCode ?? 'AED',
    waitingToBeReleased: money(dto.waitingToBeReleased),
    setAsideForBills: money(dto.setAsideForBills),
    paidOutToDate: money(dto.paidOutToDate),
    carriedForwardDeficit: money(dto.carriedForwardDeficit),
    status: dto.status,
    receiptsNeedingAttention: dto.receiptsNeedingAttention ?? 0,
    unpaidBillCount: dto.unpaidBillCount ?? 0,
    lastDistributionAt: dto.lastDistributionAt ?? null,
  }
}

export function parseReserveMovement(dto: ReserveMovementDto): ReserveMovement {
  return {
    id: dto.id,
    movementType: dto.movementType,
    // Signed, and deliberately not normalised: the sign IS the direction, and
    // dropping it would make a bill payment look like a provision.
    amount: money(dto.amount),
    balanceAfter: money(dto.balanceAfter),
    currencyCode: dto.currencyCode ?? 'AED',
    distributionId: dto.distributionId ?? null,
    billId: dto.billId ?? null,
    provisionedCostKind: dto.provisionedCostKind ?? null,
    description: dto.description ?? null,
    createdAt: dto.createdAt,
    createdByName: dto.createdByName ?? null,
  }
}

export function parsePropertyBill(dto: PropertyBillDto): PropertyBill {
  return {
    id: dto.id,
    propertyId: dto.propertyId,
    billType: dto.billType,
    otherDescription: dto.otherDescription ?? null,
    vendorName: dto.vendorName,
    invoiceNumber: dto.invoiceNumber ?? null,
    amount: money(dto.amount),
    currencyCode: dto.currencyCode ?? 'AED',
    billDate: dto.billDate,
    dueDate: dto.dueDate ?? null,
    periodCoveredFrom: dto.periodCoveredFrom ?? null,
    periodCoveredTo: dto.periodCoveredTo ?? null,
    status: dto.status,
    paidAt: dto.paidAt ?? null,
    reserveLedgerId: dto.reserveLedgerId ?? null,
    invoiceDocumentId: dto.invoiceDocumentId ?? null,
    voidedReason: dto.voidedReason ?? null,
    notes: dto.notes ?? null,
    createdAt: dto.createdAt,
  }
}

function parseProvisionedByKind(dto: ProvisionedByKindDto): ProvisionedByKind {
  return { kind: dto.kind, amount: money(dto.amount) }
}

export function parsePropertyRentLedgerDetail(
  dto: PropertyRentLedgerDetailDto,
): PropertyRentLedgerDetail {
  return {
    ...parsePropertyRentLedgerRow(dto),
    provisionedByKind: (dto.provisionedByKind ?? []).map(parseProvisionedByKind),
    reserveMovements: (dto.reserveMovements ?? []).map(parseReserveMovement),
    bills: (dto.bills ?? []).map(parsePropertyBill),
  }
}

/** Fail-closed default, same shape as the distribution-run permission parsers. */
export function parseBillPaymentPermission(dto: BillPaymentPermissionDto): BillPaymentPermission {
  return {
    canPay: dto.canPay ?? false,
    blockedReason: dto.blockedReason ?? null,
  }
}

export function parseRentLedgerList(
  dto: RentLedgerListDto,
): PaginatedResponse<PropertyRentLedgerRow> {
  const rows = dto.data ?? []
  const limit = dto.meta?.limit ?? DEFAULT_LIMIT
  const totalItems = dto.meta?.total ?? rows.length

  return {
    data: rows.map(parsePropertyRentLedgerRow),
    pagination: {
      page: dto.meta?.page ?? DEFAULT_PAGE,
      limit,
      totalItems,
      totalPages: dto.meta?.totalPages ?? (limit > 0 ? Math.ceil(totalItems / limit) : 0),
    },
  }
}
